import { z } from 'zod';
import { QualityScenarioSchema, ScenarioLinkedFlowSchema } from './scenarios';

/**
 * Requirement #8 — deterministic Scenario → existing automation (test_cases / canvas_json) mapping.
 * Does not introduce a second engine; maps onto existing STEP types used by canvas_json.steps.
 */

export const SCENARIO_AUTOMATION_MAP_STATUSES = ['mapped', 'needs_configuration', 'unsupported', 'unmapped'] as const;
export type ScenarioAutomationMapStatus = (typeof SCENARIO_AUTOMATION_MAP_STATUSES)[number];

/** Existing canvas step `type` values we emit (must match runner / STEP_TYPES). */
export const SCENARIO_AUTOMATION_CANVAS_ACTIONS = [
  'navigate',
  'click',
  'type',
  'select',
  'hover',
  'scroll',
  /** Conditional scroll — must not silently downgrade “scroll until …” to plain `scroll`. */
  'scroll-until',
  'wait',
  'assert',
  'screenshot',
  'api-request',
  'upload-file',
  'download-file',
  /** Force checkbox/radio checked — must not silently map “Check the … checkbox” to Click. */
  'check',
  /** Force checkbox/radio unchecked — must not silently map to Click. */
  'uncheck',
] as const;
export type ScenarioAutomationCanvasAction = (typeof SCENARIO_AUTOMATION_CANVAS_ACTIONS)[number];

export const ScenarioAutomationStepMappingSchema = z.object({
  scenarioStepId: z.string().uuid(),
  order: z.number().int().positive(),
  scenarioLabel: z.string(),
  status: z.enum(SCENARIO_AUTOMATION_MAP_STATUSES),
  actionType: z.enum(SCENARIO_AUTOMATION_CANVAS_ACTIONS).nullable(),
  automationNodeId: z.string().nullable().optional(),
  reason: z.string().default(''),
  /** Target / value preserved as refs (e.g. ${LoginUsers.username}) — never resolved secrets. */
  target: z.string().default(''),
  value: z.string().default(''),
  /** P0-2 — Verify kind emitted into canvas `fields.assertion` when actionType is assert. */
  assertionKind: z.string().max(40).optional(),
});
export type ScenarioAutomationStepMapping = z.infer<typeof ScenarioAutomationStepMappingSchema>;

export const ScenarioAutomationCoverageSchema = z.object({
  total: z.number().int().nonnegative(),
  mapped: z.number().int().nonnegative(),
  needsConfiguration: z.number().int().nonnegative(),
  unsupported: z.number().int().nonnegative(),
  unmapped: z.number().int().nonnegative(),
  percent: z.number().min(0).max(100),
});
export type ScenarioAutomationCoverage = z.infer<typeof ScenarioAutomationCoverageSchema>;

export const ScenarioAutomationMappingResponseSchema = z.object({
  scenarioId: z.string().uuid(),
  linked: z.array(ScenarioLinkedFlowSchema),
  primaryFlowId: z.string().uuid().nullable(),
  primaryFlowName: z.string().nullable(),
  coverage: ScenarioAutomationCoverageSchema,
  mappings: z.array(ScenarioAutomationStepMappingSchema),
  stale: z.boolean(),
  staleReason: z.string().optional(),
  lastGeneratedAt: z.string().nullable(),
  scenarioVersionAtGeneration: z.number().int().positive().nullable(),
});
export type ScenarioAutomationMappingResponse = z.infer<typeof ScenarioAutomationMappingResponseSchema>;

export const ScenarioAutomationConvertPreviewSchema = z.object({
  scenarioId: z.string().uuid(),
  title: z.string(),
  coverage: ScenarioAutomationCoverageSchema,
  mappings: z.array(ScenarioAutomationStepMappingSchema),
  canConvert: z.boolean(),
  blockingReason: z.string().optional(),
  alreadyLinked: z.boolean(),
  primaryFlowId: z.string().uuid().nullable().optional(),
});
export type ScenarioAutomationConvertPreview = z.infer<typeof ScenarioAutomationConvertPreviewSchema>;

export const ScenarioAutomationConvertRequestSchema = z.object({
  /** When true and a linked convert flow exists, refuse duplicate (default). */
  allowDuplicate: z.boolean().optional(),
  createNew: z.boolean().optional(),
});
export type ScenarioAutomationConvertRequest = z.infer<typeof ScenarioAutomationConvertRequestSchema>;

export const ScenarioAutomationConvertResponseSchema = z.object({
  scenario: QualityScenarioSchema,
  flowId: z.string().uuid(),
  flowName: z.string(),
  coverage: ScenarioAutomationCoverageSchema,
  mappings: z.array(ScenarioAutomationStepMappingSchema),
  created: z.boolean(),
});
export type ScenarioAutomationConvertResponse = z.infer<typeof ScenarioAutomationConvertResponseSchema>;

export const ScenarioAutomationUpdatePreviewSchema = z.object({
  scenarioId: z.string().uuid(),
  flowId: z.string().uuid(),
  flowName: z.string(),
  coverage: ScenarioAutomationCoverageSchema,
  mappings: z.array(ScenarioAutomationStepMappingSchema),
  added: z.array(z.string()),
  changed: z.array(z.string()),
  removed: z.array(z.string()),
  automationImpact: z.array(z.string()),
  hasManualEdits: z.boolean(),
  warning: z.string().optional(),
});
export type ScenarioAutomationUpdatePreview = z.infer<typeof ScenarioAutomationUpdatePreviewSchema>;

export const ScenarioAutomationUpdateRequestSchema = z.object({
  flowId: z.string().uuid(),
  confirmManualOverwrite: z.boolean().optional(),
});
export type ScenarioAutomationUpdateRequest = z.infer<typeof ScenarioAutomationUpdateRequestSchema>;
